import React, { PureComponent } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'

import validate from './validation'
import * as ContentActions from '../../../../actions/content'
import { notify as notifyAction } from '../../../../actions/notifications'
import { LayersForm } from '../../../../components/Screens'
import { ContentTypes } from '../../../../constants'
import { layerForm as layerFormProps } from '../../../../types'
import { ObjectUtils, FileUtils } from '../../../../utils'

// Default start date
const defaultStartDateDate = new Date()
defaultStartDateDate.setHours(0)
defaultStartDateDate.setMinutes(0)
defaultStartDateDate.setSeconds(0)
const defaultStartDate = defaultStartDateDate.getTime()

// Default end date
const defaultEndDateDate = new Date()
defaultEndDateDate.setDate(new Date().getDate() + 366)
defaultEndDateDate.setHours(0)
defaultEndDateDate.setMinutes(0)
defaultEndDateDate.setSeconds(0)
const defaultEndDate = defaultEndDateDate.getTime()

const defaultStateValues = {
    assignToAllPlaylists: true,
    progress: 0,
}

class Layers extends PureComponent {
    static propTypes = {
        editFormValue: PropTypes.func.isRequired,
        form: PropTypes.shape(layerFormProps).isRequired,
        isOpen: PropTypes.bool,
        onClickCancel: PropTypes.func,
        onFinishSubmit: PropTypes.func,
        resetUpload: PropTypes.func.isRequired,
        uploadLayer: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired,
    }

    static defaultProps = {
        isOpen: false,
        onClickCancel: () => null,
        onFinishSubmit: () => null,
    }

    constructor(props) {
        super(props)

        this.state = {
            formErrors: {
                entry_point: null,
                name: null,
                file: null,
                date_start: null,
                date_end: null,
            },
            assignToAllPlaylists: defaultStateValues.assignToAllPlaylists,
            progress: defaultStateValues.progress,
            isWorking: false,
        }

        this.onChangeFormFile = this.onChangeFormFile.bind(this)
        this.onChangeFormValue = this.onChangeFormValue.bind(this)
        this.onClickNullStartDate = this.onClickNullStartDate.bind(this)
        this.onClickNullEndDate = this.onClickNullEndDate.bind(this)
        this.onClickRemoveStartDate = this.onClickRemoveStartDate.bind(this)
        this.onClickRemoveEndDate = this.onClickRemoveEndDate.bind(this)
        this.onSubmit = this.onSubmit.bind(this)
    }

    componentDidMount() {
        this.loadData()
    }

    onChangeFormFile({ target: { name, files } }) {
        const { editFormValue, form } = this.props
        editFormValue('layers', { name, value: files })

        const { name: formFileName } = form

        if (!formFileName && files.length) {
            const { name: fileName } = files[0]
            const fileWithoutExtension = FileUtils.getFileWithoutExtension(fileName)
            editFormValue('layers', { name: 'name', value: fileWithoutExtension })
        }
    }

    onChangeFormValue({ target: { name, value } }) {
        const { editFormValue } = this.props
        editFormValue('layers', { name, value })
    }

    onClickNullStartDate() {
        this.onChangeFormValue({
            target: { name: 'date_start', value: defaultStartDate },
        })
    }

    onClickNullEndDate() {
        this.onChangeFormValue({
            target: { name: 'date_end', value: defaultEndDate },
        })
    }

    onClickRemoveStartDate() {
        this.onChangeFormValue({
            target: { name: 'date_start', value: null },
        })
    }

    onClickRemoveEndDate() {
        this.onChangeFormValue({
            target: { name: 'date_end', value: null },
        })
    }

    onProgress = (progress) => {
        this.setState({ progress })
    }

    onSubmit = async () => {
        if (!this.validate()) {
            return
        }

        this.setState({ isWorking: true })

        try {
            const { form, onFinishSubmit, uploadLayer } = this.props
            const { assignToAllPlaylists } = this.state

            const { name, file, date_start, date_end, entry_point } = form

            const { size } = file[0]

            const layer = {
                size,
                date_end,
                date_start,
                entry_point,
                name,
            }

            const saved = await uploadLayer(file[0], layer, {
                onProgress: ({ progress }) => this.setState({ progress }),
            })

            if (saved) {
                await onFinishSubmit([saved], assignToAllPlaylists)
            }

            this.loadData()
        } catch (e) {
            console.error(e)
            // TODO: Notify failure
        } finally {
            this.setState({ isWorking: false })
        }
    }

    buildOnchangeObject() {
        return {
            all_playlists: (value) => this.setState({ assignToAllPlaylists: value }),
            entry_point: this.onChangeFormValue,
            file: this.onChangeFormFile,
            name: this.onChangeFormValue,
            date_start: (value) => this.onChangeFormValue({ target: { name: 'date_start', value } }),
            date_end: (value) => this.onChangeFormValue({ target: { name: 'date_end', value } }),
        }
    }

    loadData() {
        const { resetUpload } = this.props
        resetUpload(ContentTypes.LAYERS)
        const { assignToAllPlaylists, progress } = defaultStateValues
        this.setState({ assignToAllPlaylists, progress })
    }

    /**
     * Form Validation
     */
    validate() {
        const { form } = this.props
        const formErrors = validate(form)
        this.setState({ formErrors })
        return ObjectUtils.isObjValuesEmpty(formErrors)
    }

    render() {
        const { form, isOpen, onClickCancel, t } = this.props
        const { assignToAllPlaylists, formErrors, progress, isWorking } = this.state
        return (
            <LayersForm
                assignToAllPlaylists={assignToAllPlaylists}
                errors={formErrors}
                isOpen={isOpen}
                isWorking={isWorking}
                onChangeForm={this.buildOnchangeObject()}
                onClickCancel={onClickCancel}
                onClickSubmit={this.onSubmit}
                onClickNullStartDate={this.onClickNullStartDate}
                onClickNullEndDate={this.onClickNullEndDate}
                onClickRemoveStartDate={this.onClickRemoveStartDate}
                onClickRemoveEndDate={this.onClickRemoveEndDate}
                progress={progress}
                values={form}
                t={t}
            />
        )
    }
}

const mapStateToProps = ({
    content: {
        layers: data,
        form: { layers: form },
        pagination: {
            layers: { currentPage, to, total },
        },
    },
}) => ({
    data,
    form,
    pagination: {
        currentPage,
        to,
        total,
    },
})

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            ...ContentActions,
            notify: notifyAction,
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Layers))
